export interface GenerationFailure {
  errorCode?: string;
  creditsRefunded?: boolean;
  retryable?: boolean;
}

export const GENERATION_ERROR_CODES = [
  "GENERATION_UNAVAILABLE",
  "GENERATION_REJECTED",
  "GENERATION_FAILED",
  "INSUFFICIENT_CREDITS",
  "RATE_LIMITED",
  "GENERATION_TIMEOUT",
  "TEMPORARILY_UNAVAILABLE",
] as const;

export const GENERATION_FAILURE_PHASES = ["image_decode", "edge_function"] as const;

export type GenerationErrorCode = (typeof GENERATION_ERROR_CODES)[number] | "unknown";
export type GenerationFailurePhase = (typeof GENERATION_FAILURE_PHASES)[number];

export type GenerationFailureAnalytics = {
  errorCode: GenerationErrorCode;
  creditsRefunded?: boolean;
  retryable?: boolean;
  phase?: GenerationFailurePhase;
};

export function normalizeGenerationErrorCode(value: unknown): GenerationErrorCode {
  return typeof value === "string" && (GENERATION_ERROR_CODES as readonly string[]).includes(value)
    ? value as GenerationErrorCode
    : "unknown";
}

export function normalizeGenerationFailurePhase(value: unknown): GenerationFailurePhase | undefined {
  return typeof value === "string" && (GENERATION_FAILURE_PHASES as readonly string[]).includes(value)
    ? value as GenerationFailurePhase
    : undefined;
}

export class CustomerGenerationError extends Error {
  readonly errorCode: GenerationErrorCode;
  readonly creditsRefunded?: boolean;
  readonly retryable?: boolean;
  readonly phase?: GenerationFailurePhase;

  constructor(failure: GenerationFailure | null, phase?: unknown) {
    super(getCustomerGenerationError(failure));
    this.name = "CustomerGenerationError";
    this.errorCode = normalizeGenerationErrorCode(failure?.errorCode);
    if (typeof failure?.creditsRefunded === "boolean") this.creditsRefunded = failure.creditsRefunded;
    if (typeof failure?.retryable === "boolean") this.retryable = failure.retryable;
    this.phase = normalizeGenerationFailurePhase(phase);
  }
}

interface FunctionErrorLike {
  context?: {
    clone?: () => { json: () => Promise<unknown> };
    json?: () => Promise<unknown>;
  };
}

function isFailurePayload(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function inferErrorCode(payload: Record<string, unknown>): GenerationErrorCode {
  if ("errorCode" in payload) return normalizeGenerationErrorCode(payload.errorCode);
  if (typeof payload.error !== "string") return "unknown";

  const message = payload.error.toLowerCase();
  if (message.includes("insufficient credits")) return "INSUFFICIENT_CREDITS";
  if (message.includes("rate limit")) return "RATE_LIMITED";
  if (message.includes("timeout") || message.includes("trop de temps")) return "GENERATION_TIMEOUT";
  if (message.includes("no image in response") || message.includes("no parts in response")) {
    return "GENERATION_UNAVAILABLE";
  }
  if (message.includes("worker_limit") || message.includes("failed to send")) {
    return "TEMPORARILY_UNAVAILABLE";
  }
  return "unknown";
}

export async function parseFunctionFailure(error: unknown): Promise<GenerationFailure | null> {
  if (!isFailurePayload(error)) return null;

  const context = (error as FunctionErrorLike).context;
  if (!context) return null;

  try {
    const response = typeof context.clone === "function" ? context.clone() : context;
    if (typeof response.json !== "function") return null;
    const payload = await response.json();
    if (!isFailurePayload(payload)) return null;

    return {
      errorCode: inferErrorCode(payload),
      ...(typeof payload.creditsRefunded === "boolean" ? { creditsRefunded: payload.creditsRefunded } : {}),
      ...(typeof payload.retryable === "boolean" ? { retryable: payload.retryable } : {}),
    };
  } catch {
    return null;
  }
}

export function shouldRetryGeneration(failure: GenerationFailure | null): boolean {
  return failure?.retryable === true && failure.creditsRefunded === true;
}

export function getGenerationFailureAnalytics(
  error: unknown,
  fallbackPhase?: unknown,
): GenerationFailureAnalytics {
  if (error instanceof CustomerGenerationError) {
    return {
      errorCode: error.errorCode,
      ...(typeof error.creditsRefunded === "boolean" ? { creditsRefunded: error.creditsRefunded } : {}),
      ...(typeof error.retryable === "boolean" ? { retryable: error.retryable } : {}),
      ...(error.phase ? { phase: error.phase } : {}),
    };
  }

  const phase = normalizeGenerationFailurePhase(fallbackPhase);
  return { errorCode: "unknown", ...(phase ? { phase } : {}) };
}

export function getCustomerGenerationError(failure: GenerationFailure | null): string {
  if (failure?.errorCode === "INSUFFICIENT_CREDITS") {
    return "Crédits insuffisants. Veuillez recharger votre compte.";
  }
  if (failure?.errorCode === "RATE_LIMITED") {
    return "Trop de créations ont été demandées. Veuillez patienter une minute.";
  }
  if (failure?.errorCode === "GENERATION_TIMEOUT") {
    return "La création a pris trop de temps. Votre crédit a été recrédité si nécessaire. Veuillez réessayer.";
  }
  if (failure?.errorCode === "GENERATION_REJECTED") {
    return failure.creditsRefunded
      ? "La création n’a pas pu aboutir avec cette photo. Votre crédit a été automatiquement recrédité. Veuillez essayer une autre photo."
      : "La création n’a pas pu aboutir avec cette photo. Veuillez essayer une autre photo.";
  }
  if (failure?.creditsRefunded) {
    return "La création n’a pas pu aboutir. Votre crédit a été automatiquement recrédité. Veuillez réessayer.";
  }

  return "La création n’a pas pu aboutir. Veuillez réessayer dans quelques instants.";
}

export function getSafeGenerationErrorMessage(error: unknown): string {
  if (error instanceof CustomerGenerationError) return error.message;
  return "La création n’a pas pu aboutir. Veuillez réessayer dans quelques instants.";
}
